import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AlertController, ModalController } from '@ionic/angular';
import { AssetInScene } from 'src/app/models/AssetInScene';
import { MediaAsset } from 'src/app/models/assets/MediaAsset';
import { QuestionAsset } from 'src/app/models/assets/QuestionAsset';
import { QuestionResponse } from 'src/app/models/assets/QuestionResponse';
import { Scene } from 'src/app/models/Scene';
import { SceneAssetRelation } from 'src/app/models/scene-asset-relations/SceneAssetRelation';
import { AssetManagementService } from 'src/app/services/asset-management.service';
import { AssetTypeService } from 'src/app/services/asset-type.service';
import { VrEventService } from 'src/app/services/vr-event.service';

@Component({
  selector: 'app-question-asset-wizard',
  templateUrl: './question-asset-wizard.component.html',
  styleUrls: ['./question-asset-wizard.component.scss'],
})
export class QuestionAssetWizardComponent implements OnInit {

  @Input('assetType') assetType: string;
  @Input('assetInScene') assetInScene: AssetInScene;
  @Input('existingAssetsInScene') existingAssetsInScene: AssetInScene[] = [];
  @Input('existingScenes') existingScenes: Scene[] = [];
  @Input('editing') editing: boolean = false;

  public assetsOnAssetManagementPage: MediaAsset[] = [];

  public steps: any[];
  public currentStepInfo: any = {
    currentStepIndex: 0,
    currentSubStepIndex: 0,
    currentQuestionResponseIndex: 0,
    currentResponse: null,
    questionActions: false,
    answerActions: false,
    currentQuestionEvents: [],
    currentQuestionRules: [],
    addMedia: false,
    addMediaFromMediaManagementPage: false,
    editResponseObjectProperties: false,
    responseObjectType: "",
    responseObject: null,
    responseObjectToSave: null,
    assetIDToCopy: -1,
    needHelp: false
  }
  public questionProperties: any = null;

  public maxQuestionLength: number = 0;
  public maxResponsesLength: number = 0;

  constructor(
    private sanitizer: DomSanitizer,
    public modalController: ModalController,
    public assetTypeService: AssetTypeService,
    public vrEventService: VrEventService,
    public assetManagementService: AssetManagementService,
    private alertController: AlertController
  ) {
    this.steps = [{
      index: 1,
      description: "Introduction",
      complete: false,
      active: true
    }, {
      index: 2,
      description: "Question Properties",
      complete: false,
      active: false
    }];
    this.questionProperties = {
      questionResponsesTextFontFamily: "../assets/fonts/opensans/OpenSans-Regular-msdf.json",
      questionResponsesTextSize: 29,
      questionResponsesTextColour: "#000000",
      responseFiles: []
    };
  }

  ngOnInit() {
    this.assetManagementService.getAssetsOnManagementPage().then(() => {
      this.assetsOnAssetManagementPage = this.assetManagementService.assets;
    });
    if (this.editing == true) {
      let questionAsset = this.assetInScene.baseAsset as QuestionAsset;

      this.questionProperties.questionResponsesTextFontFamily = questionAsset.responses[0].fontFamily;
      this.questionProperties.questionResponsesTextSize = questionAsset.responses[0].textSize;
      this.questionProperties.questionResponsesTextColour = questionAsset.responses[0].textColour;
    }

    this.getQuestionAsset().responses.forEach(r => {
      this.questionProperties.responseFiles.push({imageFiles: null, videoFiles: null, audioFiles: null});
    });
  }

  getQuestionAsset(): QuestionAsset {
    return this.assetInScene.baseAsset as QuestionAsset;
  }


  async closeModal() {
    const alert = await this.alertController.create({
      header: 'Are you sure?',
      message: 'Are you sure you want to leave? Any changes will not be saved.',
      cssClass: 'leave-alert quizard-alert quizard-leave-alert',
      buttons: [{
        text: 'Yes',
        cssClass: '',
        handler: () => {
          this.modalController.dismiss();
        }
      }, {
        text: 'No',
        cssClass: '',
        handler: () => {
          alert.dismiss();
        }
      }
      ]
    });
    await alert.present();
  }

  exitHelp() {
    this.currentStepInfo.needHelp = false;
  }

  goToHelp() {
    this.currentStepInfo.needHelp = true;
  }

  addResponse(baseAsset: QuestionAsset, i: number = 0) {
    let response: QuestionResponse = new QuestionResponse();
    let maxResponseIndex = 0;

    response.isCorrect = i == 1 ? true : false;
    response.text = 'Default Response ' + (i >= 1 ? i : '');
    response.textSize = 30;
    response.hasImage = false;
    response.imageSceneAssetRelationId = 0;
    response.hasVideo = false;
    response.videoSceneAssetRelationId = 0;
    response.hasAudio = false;
    response.audioSceneAssetRelationId = 0;
    response.hasLinkedScene = false;
    response.linkedSceneID = 0;
    response.fontFamily = "../assets/fonts/opensans/OpenSans-Regular-msdf.json";
    response.assets = [];

    for (let i: number = 0; i < baseAsset.responses.length; i++) {
      if (baseAsset.responses[i].questionResponseIndex > maxResponseIndex)
        maxResponseIndex = baseAsset.responses[i].questionResponseIndex;
    }

    response.questionResponseIndex = maxResponseIndex + 1;

    this.addResponseObjects(response);
    this.questionProperties.responseFiles.push({imageFiles: null, videoFiles: null, audioFiles: null});

    baseAsset.responses.push(response);
  }

  addResponseObjects(response: QuestionResponse) {
    let imageTypeId = this.assetTypeService.getId('Image');
    let videoTypeId = this.assetTypeService.getId('Video');
    let audioTypeId = this.assetTypeService.getId('Audio');
    let imageAsset = new MediaAsset();
    let videoAsset = new MediaAsset();
    let audioAsset = new MediaAsset();
    let imageAssetInScene = new AssetInScene();
    let videoAssetInScene = new AssetInScene();
    let audioAssetInScene = new AssetInScene();

    imageAsset.assetTypeId = imageTypeId;
    imageAsset.name = 'Default Response - Image';
    videoAsset.assetTypeId = videoTypeId;
    videoAsset.name = 'Default Response - Video';
    audioAsset.assetTypeId = audioTypeId;
    audioAsset.name = 'Default Response - Audio';

    imageAssetInScene.baseAsset = imageAsset;
    imageAssetInScene.sceneAssetRelation = new SceneAssetRelation();
    this.setUpAssetScale(imageAssetInScene.sceneAssetRelation, '1.5', '0.75', '0.1');
    this.setUpAssetPosition(imageAssetInScene.sceneAssetRelation, '0', '0.525', '0.5');
    imageAssetInScene.toBeRemoved = false;
    imageAssetInScene.events = [];

    videoAssetInScene.baseAsset = videoAsset;
    videoAssetInScene.sceneAssetRelation = new SceneAssetRelation();
    this.setUpAssetScale(videoAssetInScene.sceneAssetRelation, '1.5', '0.75', '0.1');
    this.setUpAssetPosition(videoAssetInScene.sceneAssetRelation, '0', '0.525', '0.5');
    videoAssetInScene.toBeRemoved = false;
    videoAssetInScene.events = [];

    audioAssetInScene.baseAsset = audioAsset;
    audioAssetInScene.sceneAssetRelation = new SceneAssetRelation();
    this.setUpAssetScale(audioAssetInScene.sceneAssetRelation, '0.5', '0.25', '0.1');
    this.setUpAssetPosition(audioAssetInScene.sceneAssetRelation, '0', '0.25', '0.5');
    audioAssetInScene.toBeRemoved = false;
    audioAssetInScene.events = [];

    response.assets.push(imageAssetInScene);
    response.assets.push(videoAssetInScene);
    response.assets.push(audioAssetInScene);
  }

  goToStep(step: number) {
    if (this.currentStepInfo.currentStepIndex == step)
      return;

    this.steps[this.currentStepInfo.currentStepIndex].complete = true;

    this.currentStepInfo.currentStepIndex = step;
    this.currentStepInfo.currentSubStepIndex = 0;
    this.currentStepInfo.currentQuestionIndex = 0;
    this.steps[this.currentStepInfo.currentStepIndex].active = true;
    this.steps[this.currentStepInfo.currentStepIndex].complete = false;
    this.currentStepInfo.questionActions = false;
    this.currentStepInfo.answerActions = false;
    this.currentStepInfo.addMedia = false,
      this.currentStepInfo.addMediaFromMediaManagementPage = false,
      this.currentStepInfo.editResponseObjectProperties = false,
      this.currentStepInfo.needHelp = false;
  }

  goToSubStep(step: number) {
    if (this.currentStepInfo.currentSubStepIndex == step)
      return;

    this.currentStepInfo.currentSubStepIndex = step;
    this.currentStepInfo.questionActions = false;
    this.currentStepInfo.answerActions = false;
    this.currentStepInfo.addMedia = false,
      this.currentStepInfo.addMediaFromMediaManagementPage = false,
      this.currentStepInfo.editResponseObjectProperties = false,
      this.currentStepInfo.needHelp = false;
  }

  nextStep() {
    if (this.currentStepInfo.currentStepIndex == 0) {
      this.goToStep(this.currentStepInfo.currentStepIndex + 1);
    } else {
      this.goToStep(this.currentStepInfo.currentStepIndex + 1);
    }
  }

  saveSingleQuestion() {
    if (this.validateSingleQuestionProperties() == false) {
      return;
    }

    let data: any = {
      assetForScene: this.assetInScene,
      assetType: this.assetType,
      questionProperties: this.questionProperties
    }
    this.modalController.dismiss(data);
  }

  validateSingleQuestionProperties(): boolean {
    let questionAsset = this.getQuestionAsset();

    if (!questionAsset.name || questionAsset.name.trim().length == 0) {
      this.goToStep(0);
      return false;
    }

    if (!questionAsset.text || questionAsset.text.trim().length == 0 ||
      (questionAsset.hasFeedback == true && (!questionAsset.correctMessage || questionAsset.correctMessage.trim().length == 0
        || !questionAsset.incorrectMessage || questionAsset.incorrectMessage.trim().length == 0))
    ) {
      this.goToStep(1);
      return false;
    }

    for (let j: number = 0; j < questionAsset.responses.length; j++) {
      let questionResponse = questionAsset.responses[j];

      if (!questionResponse.assets || (questionResponse.assets && questionResponse.assets.length == 0)) {
        questionResponse.assets = [];
        this.addResponseObjects(questionResponse);
      }

      if (!questionResponse.text || questionResponse.text.trim().length == 0) {
        this.goToStep(1);
        return false;
      }

      for (let k: number = 0; k < questionResponse.assets.length; k++) {
        let assetInResponse = questionResponse.assets[k];
        let assetType = this.assetTypeService.getType(assetInResponse.baseAsset.assetTypeId);

        if (assetInResponse.baseAsset.name.trim().length == 0) {
          assetInResponse.baseAsset.name = 'Default Response - ' + assetType;
        }

        this.setUpAssetPosition(assetInResponse.sceneAssetRelation, 
          String(assetInResponse.sceneAssetRelation.xPos), 
          String(assetInResponse.sceneAssetRelation.yPos), 
          String(assetInResponse.sceneAssetRelation.zPos));
        this.setUpAssetScale(assetInResponse.sceneAssetRelation, 
          String(assetInResponse.sceneAssetRelation.xScale), 
          String(assetInResponse.sceneAssetRelation.yScale), 
          String(assetInResponse.sceneAssetRelation.zScale));
      }
    }

    this.validateQuestionFontSize();
    this.validateResponsesFontSize();

    questionAsset.backgroundOpacity = Number(questionAsset.backgroundOpacity);
    questionAsset.textSize = Number(questionAsset.textSize);
    questionAsset.score = Number(questionAsset.score);

    return true;
  }

  validateQuestionFontSize() {
    this.maxQuestionLength = this.getQuestionAsset().text.length;
    if (this.maxQuestionLength > 125 && (55 - this.getQuestionAsset().textSize) > 26)
      this.getQuestionAsset().textSize = 55 - 26;
  }

  validateResponsesFontSize() {
    for (let i: number = 0; i < this.getQuestionAsset().responses.length; i++) {
      let response = this.getQuestionAsset().responses[i];
      this.maxResponsesLength = response.text.length > this.maxResponsesLength ? response.text.length : this.maxResponsesLength;
    }
    if (this.maxResponsesLength > 60 && (55 - this.questionProperties.questionResponsesTextSize) > 20)
      this.questionProperties.questionResponsesTextSize = 55 - 20;
  }

  previousStep() {
    if (this.currentStepInfo.currentStepIndex == 0) {

    } else if (this.currentStepInfo.currentStepIndex > 0) {
      this.goToStep(this.currentStepInfo.currentStepIndex - 1);
      this.steps[this.currentStepInfo.currentStepIndex].complete = false;
    }
  }

  goBackToQuestion() {
    this.currentStepInfo.questionActions = false;
    this.currentStepInfo.answerActions = false;
    this.currentStepInfo.addMedia = false;
    this.currentStepInfo.needHelp = false;
  }

  saveQuestionActions() {
    this.assetInScene.events = this.currentStepInfo.currentQuestionEvents;
    this.assetInScene.questionRules = this.currentStepInfo.currentQuestionRules;

    this.goBackToQuestion();
  }

  saveCurrentResponseMedia() {
    let currentQuestion: QuestionAsset = this.assetInScene.baseAsset as QuestionAsset;
    let currentResponse: any = currentQuestion.responses[this.currentStepInfo.currentQuestionResponseIndex];
    //currentQuestion.responses[this.currentStepInfo.currentQuestionResponseIndex] = this.currentStepInfo.currentResponse;
    currentResponse.hasImage = this.currentStepInfo.currentResponse.hasImage;
    currentResponse.imageUploaded = this.currentStepInfo.currentResponse.imageUploaded;
    currentResponse.imageFiles = this.currentStepInfo.currentResponse.imageFiles;
    currentResponse.imageFromMediaManagementPageId = this.currentStepInfo.currentResponse.imageFromMediaManagementPageId;
    currentResponse.hasVideo = this.currentStepInfo.currentResponse.hasVideo;
    currentResponse.videoUploaded = this.currentStepInfo.currentResponse.videoUploaded;
    currentResponse.videoFiles = this.currentStepInfo.currentResponse.videoFiles;
    currentResponse.videoCompress = this.currentStepInfo.currentResponse.videoCompress;
    currentResponse.videoFromMediaManagementPageId = this.currentStepInfo.currentResponse.videoFromMediaManagementPageId;
    currentResponse.hasAudio = this.currentStepInfo.currentResponse.hasAudio;
    currentResponse.audioUploaded = this.currentStepInfo.currentResponse.audioUploaded;
    currentResponse.audioFiles = this.currentStepInfo.currentResponse.audioFiles;
    currentResponse.audioFromMediaManagementPageId = this.currentStepInfo.currentResponse.audioFromMediaManagementPageId;
    currentResponse.hasLinkedScene = this.currentStepInfo.currentResponse.hasLinkedScene;
    currentResponse.linkedSceneID = this.currentStepInfo.currentResponse.linkedSceneID;

    if(!currentResponse.assets || (currentResponse.assets && currentResponse.assets.length == 0)){
      currentResponse.assets = this.currentStepInfo.currentResponse.assets;
    }else{
      for(let i:number = 0; i < currentResponse.assets.length; i++){
        let assetInResponse = currentResponse.assets[i];
        let assetInResponseToCopy = this.currentStepInfo.currentResponse.assets[i];

        assetInResponse.baseAsset.mediaFileExtension = assetInResponseToCopy.baseAsset.mediaFileExtension;
        assetInResponse.baseAsset.name = assetInResponseToCopy.baseAsset.name;
        assetInResponse.baseAsset.src = assetInResponseToCopy.baseAsset.src;
        assetInResponse.baseAsset.srcToView = assetInResponseToCopy.baseAsset.srcToView;

        assetInResponse.sceneAssetRelation = assetInResponseToCopy.sceneAssetRelation;
      }
    }

    this.questionProperties.responseFiles[this.currentStepInfo.currentQuestionResponseIndex] = {
      imageFiles: this.currentStepInfo.currentResponse.imageFiles ? this.currentStepInfo.currentResponse.imageFiles : this.questionProperties.responseFiles[this.currentStepInfo.currentQuestionResponseIndex].imageFiles,
      videoFiles: this.currentStepInfo.currentResponse.videoFiles ? this.currentStepInfo.currentResponse.videoFiles : this.questionProperties.responseFiles[this.currentStepInfo.currentQuestionResponseIndex].videoFiles,
      audioFiles: this.currentStepInfo.currentResponse.audioFiles ? this.currentStepInfo.currentResponse.audioFiles : this.questionProperties.responseFiles[this.currentStepInfo.currentQuestionResponseIndex].audioFiles,
    };

    this.goBackToQuestion();

    this.currentStepInfo.currentResponse = null;
  }

  saveSelectedResponseMediaAsset() {
    if (Number(this.currentStepInfo.assetIDToCopy) <= 0) {
      return;
    }

    let mediaAsset = this.assetsOnAssetManagementPage.find(a => a.baseAssetId == this.currentStepInfo.assetIDToCopy);
    switch (this.currentStepInfo.responseObjectType) {
      case 'image': {
        let imageAsset: any = this.currentStepInfo.responseObject.baseAsset as MediaAsset;

        imageAsset.mediaFileExtension = mediaAsset.mediaFileExtension;
        imageAsset.name = mediaAsset.name;
        imageAsset.src = mediaAsset.src;
        imageAsset.srcToView = this.sanitizer.bypassSecurityTrustUrl(imageAsset.src);

        this.currentStepInfo.currentResponse.hasImage = true;
        this.currentStepInfo.currentResponse.imageUploaded = false;
        this.currentStepInfo.currentResponse.imageFiles = null;
        this.currentStepInfo.currentResponse.imageFromMediaManagementPageId = this.currentStepInfo.assetIDToCopy;

        break;
      }
      case 'video': {
        let videoAsset: any = this.currentStepInfo.responseObject.baseAsset as MediaAsset;

        videoAsset.mediaFileExtension = mediaAsset.mediaFileExtension;
        videoAsset.name = mediaAsset.name;
        videoAsset.src = mediaAsset.src;
        videoAsset.srcToView = this.sanitizer.bypassSecurityTrustUrl(videoAsset.src);

        this.currentStepInfo.currentResponse.hasVideo = true;
        this.currentStepInfo.currentResponse.videoUploaded = false;
        this.currentStepInfo.currentResponse.videoCompress = true;
        this.currentStepInfo.currentResponse.videoFiles = null;
        this.currentStepInfo.currentResponse.videoFromMediaManagementPageId = this.currentStepInfo.assetIDToCopy;

        break;
      }
      case 'audio': {
        let audioAsset: MediaAsset = this.currentStepInfo.responseObject.baseAsset as MediaAsset;

        audioAsset.mediaFileExtension = mediaAsset.mediaFileExtension;
        audioAsset.name = mediaAsset.name;
        audioAsset.src = mediaAsset.src

        this.currentStepInfo.currentResponse.hasAudio = true;
        this.currentStepInfo.currentResponse.audioUploaded = false;
        this.currentStepInfo.currentResponse.audioFiles = null;
        this.currentStepInfo.currentResponse.audioFromMediaManagementPageId = this.currentStepInfo.assetIDToCopy;
        break;
      }
    }

    this.goBackToChangeMedia();
  }

  saveCurrentResponseMediaProperties() {
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.xPos = this.currentStepInfo.responseObject.sceneAssetRelation.xPos;
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.yPos = this.currentStepInfo.responseObject.sceneAssetRelation.yPos;
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.zPos = this.currentStepInfo.responseObject.sceneAssetRelation.zPos;
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.position = this.currentStepInfo.responseObject.sceneAssetRelation.position;
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.xScale = this.currentStepInfo.responseObject.sceneAssetRelation.xScale;
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.yScale = this.currentStepInfo.responseObject.sceneAssetRelation.yScale;
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.zScale = this.currentStepInfo.responseObject.sceneAssetRelation.zScale;
    this.currentStepInfo.responseObjectToSave.sceneAssetRelation.scale = this.currentStepInfo.responseObject.sceneAssetRelation.scale;

    this.goBackToChangeMedia();
  }

  goBackToChangeMedia() {
    this.currentStepInfo.addMediaFromMediaManagementPage = false;
    this.currentStepInfo.editResponseObjectProperties = false;
    this.currentStepInfo.assetIDToCopy = -1;
  }

  setUpAssetScale(sceneAssetRelation, x: string, y: string, z: string) {
    sceneAssetRelation.xScale = x;
    sceneAssetRelation.yScale = y;
    sceneAssetRelation.zScale = z;
    sceneAssetRelation.scale = x + ' ' + y + ' ' + z;
  }

  setUpAssetPosition(sceneAssetRelation, x: string, y: string, z: string) {
    sceneAssetRelation.xPos = x.toString();
    sceneAssetRelation.yPos = y.toString();
    sceneAssetRelation.zPos = z.toString();
    sceneAssetRelation.position = x + ' ' + y + ' ' + z;
  }


}
